import type { GraphQLResult } from 'aws-amplify/api'
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  createSectionMtn,
  deleteSectionMtn,
  updateSectionMtn,
} from '@/services/graphql/blueprint'
import { SectionInput } from '@/types'
import {
  type BlueprintCacheEntry,
  updateBlueprintCacheSections,
} from './blueprint.cache'

const client = getAmplifyClient()

type SectionCreate = {
  upsertType: 'create' | 'update'
  input: SectionInput
}

type SectionDelete = {
  upsertType: 'delete'
  input: { id: string; blueprintSectionsId: string }
}

type SectionParams = SectionCreate | SectionDelete

export const useUpsertSection = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createSection'],
    mutationFn: upsertSection,
    onMutate: (variables) =>
      updateBlueprintSectionsCache(queryClient, variables),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['blueprint', variables.input.blueprintSectionsId],
      })
      queryClient.invalidateQueries({
        queryKey: ['section', variables.input.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['sections'],
      })
    },
  })
}

export const updateBlueprintSectionsCache = async (
  queryClient: QueryClient,
  variables: SectionParams,
): Promise<void> => {
  const { upsertType, input } = variables

  await Promise.all([
    queryClient.cancelQueries({
      queryKey: ['blueprint', input.blueprintSectionsId],
    }),
    queryClient.cancelQueries({
      queryKey: ['section', input.id],
      exact: true,
    }),
  ])

  queryClient.setQueryData(
    ['blueprint', input.blueprintSectionsId],
    (data: BlueprintCacheEntry | undefined) =>
      updateBlueprintCacheSections(data, (sections) => {
        if (upsertType === 'delete') {
          return sections.filter((section) => section.id !== input.id)
        }

        if (upsertType === 'create') {
          return [...sections, input]
        }

        return sections.map((section) =>
          section.id === input.id ? { ...section, ...input } : section,
        )
      }),
  )
  if (upsertType === 'create') {
    queryClient.setQueryData(['section', input.id], {
      ...input,
      shapes: { items: [] },
    })
  } else if (upsertType === 'delete') {
    queryClient.removeQueries({
      queryKey: ['section', input.id],
      exact: true,
    })
  } else if (upsertType === 'update') {
    queryClient.setQueryData(['section', input.id], (data) => {
      if (!data || typeof data !== 'object') return data

      return {
        ...data,
        ...input,
      }
    })
  }
}

const upsertSection = async (variables: SectionParams) => {
  switch (variables.upsertType) {
    case 'create':
      return createSection(variables.input)
    case 'update':
      return updateSection(variables.input)
    case 'delete':
      return deleteSection(variables.input)
  }
}

const createSection = async (input: SectionInput) => {
  const result = (await client.graphql({
    query: createSectionMtn,
    variables: { input },
  })) as GraphQLResult<{ createSection: { createdAt: string } }>
  return result.data?.createSection
}

const updateSection = async (input: SectionInput) => {
  const result = (await client.graphql({
    query: updateSectionMtn,
    variables: { input },
  })) as GraphQLResult<{ updateSection: { updatedAt: string } }>
  return result.data?.updateSection
}

const deleteSection = async ({
  id,
}: {
  id: string
  blueprintSectionsId: string
}) => {
  const result = (await client.graphql({
    query: deleteSectionMtn,
    variables: { input: { id } },
  })) as GraphQLResult<{ deleteSection: { deletedAt: string } }>
  return result.data?.deleteSection
}
